import React, { FunctionComponent, useRef, useState } from "react";
import { Col, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import {
  DataPeriod,
  EvidenceColumn,
  EvidenceColumnGroup,
  Gender,
  GenderDisplayedNames,
  Race,
  RaceDisplayedNames,
} from "../../../../../../store/onboarding/types";
import { Grade } from "../../../../../../store/groups/types";
import BellowCell from "./BelowCell";
import AtOrAboveCell from "./AtOrAboveCell";
import DemographicGroupRows from "./DemographicGroupRows";
import usePDFGenerate from "../../../../../common/pdf-generate/usePDFGenerate";
import { BenchmarkStats, SchoolOption } from "../hooks/useEquityBenchmarkData";

type OwnProps = {
  dataPeriod?: DataPeriod;
  evidenceColumnGroup: EvidenceColumnGroup;
  displayedEvidenceColumns: EvidenceColumn[];
  selectedGrade?: { value: Grade; label: string };
  selectedSchool: number | undefined;
  schoolOptions: SchoolOption[];
  allStudents: number[];
  raceDictionary: Record<string, number[]>;
  specialEducationDictionary: Record<string, number[]>;
  ELLDictionary: Record<string, number[]>;
  SLDDictionary: Record<string, number[]>;
  genderDictionary: Record<string, number[]>;
  freeLunchDictionary: Record<string, number[]>;
  stats: Record<number, BenchmarkStats | null>;
  isPdfExportReady: boolean;
};

type Props = OwnProps;

/**
 * Renders the legend, PDF-export button, and the full equity benchmarks
 * table (including the hidden PDF-only header block). Extracted from
 * EquityBenchmarksModalContent with no logic changes.
 */
const EquityBenchmarkTable: FunctionComponent<Props> = (props) => {
  const {
    dataPeriod,
    evidenceColumnGroup,
    displayedEvidenceColumns,
    selectedGrade,
    selectedSchool,
    schoolOptions,
    allStudents,
    raceDictionary,
    specialEducationDictionary,
    ELLDictionary,
    SLDDictionary,
    genderDictionary,
    freeLunchDictionary,
    stats,
    isPdfExportReady,
  } = props;

  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [allStudentsHeight, setAllStudentsHeight] = useState<number>(0);
  // Controls visibility of the PDF-only header block (assessment/data
  // period/grade/school info) that's only shown while the PDF is being
  // captured, and hidden the rest of the time.
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

  const benchmarkRef = useRef<HTMLDivElement | null>(null);
  const { reportGenerate } = usePDFGenerate();

  const handleReportGenerate = () => {
    const fileName: string = `Equity Benchmarks.pdf`;
    benchmarkRef.current?.classList.remove(
      "equity-benchmark-table-max-height-wrapper"
    );
    setIsGeneratingPdf(true);
    reportGenerate(benchmarkRef, fileName, "equityBenchmarks");
    setTimeout(() => {
      setIsGeneratingPdf(false);
      benchmarkRef.current?.classList.add(
        "equity-benchmark-table-max-height-wrapper"
      );
    }, 1);

    return true;
  };

  return (
    <>
      <br />
      <button
        className="float-right whiteBtnSm mb-2"
        onClick={handleReportGenerate}
        disabled={isGeneratingPdf}
        aria-busy={isGeneratingPdf}
      >
        {isGeneratingPdf ? (
          <>
            <span
              className="spinner-border spinner-border-sm mr-2"
              role="status"
              aria-hidden="true"
            />
            Generating PDF...
          </>
        ) : (
          "Generate PDF"
        )}
      </button>
      <div className="row mb-2">
        <div style={{ cursor: "default" }} className="d-flex col-auto">
          <div
            style={{
              background: "rgb(255, 209, 218)",
              cursor: "inherit",
            }}
            className="sketchColorPickerColor col-auto"
          />
          <div className="font-weight-bold ml-2 pdfFontFamily">Well Below/Below</div>
        </div>
        <div style={{ cursor: "default" }} className="d-flex col-auto">
          <div
            style={{
              background: "rgb(191, 249, 221)",
              cursor: "inherit",
            }}
            className="sketchColorPickerColor col-auto"
          />
          <div className="font-weight-bold ml-2 pdfFontFamily">At/Above</div>
        </div>
      </div>
      <div
        className={
          "equity-benchmark-table-max-height-wrapper" +
          (isPdfExportReady ? " benchmark-modal-pdf-class" : "")
        }
        ref={benchmarkRef}
      >
        <div
          id="equity-benchmark-table"
          style={{
            display: isGeneratingPdf ? "inline-block" : "none",
            width: "100%",
          }}
        >
          {/* Title */}
          <Row className="mb-4">
            <Col className="text-center">
              <h2 className="font-weight-bold pdfFontFamily">Equity Benchmarks</h2>
            </Col>
          </Row>

          {/* Legend */}
          <Row className="justify-content-center mb-4">
            <Col xs="auto">
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#FFD1DA",
                    borderRadius: 2,
                  }}
                />
                <span className="ml-2 font-weight-bold pdfFontFamily">
                  Well Below/Below
                </span>
              </div>
            </Col>

            <Col xs="auto">
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#BFF9DD",
                    borderRadius: 2,
                  }}
                />
                <span className="ml-2 font-weight-bold pdfFontFamily">At/Above</span>
              </div>
            </Col>
          </Row>

          {/* Information Table */}
          <Table bordered className="mb-4" style={{ width: "100%" }}>
            <tbody>
              <tr>
                <td>
                  <strong className="pdfFontFamily">Assessment:</strong>{" "}
                  <span className="pdfFontFamily">
                    {evidenceColumnGroup?.assessment?.name}
                  </span>
                </td>
              </tr>

              <tr className="pdf-alt-row">
                <td>
                  <strong className="pdfFontFamily">Data Period:</strong>{" "}
                  <span className="pdfFontFamily">{dataPeriod?.name}</span>
                </td>
              </tr>

              <tr>
                <td>
                  <strong className="pdfFontFamily">Grade:</strong>{" "}
                  <span className="pdfFontFamily">{selectedGrade?.label}</span>
                </td>
              </tr>

              <tr className="pdf-alt-row">
                <td>
                  <strong className="pdfFontFamily">School:</strong>{" "}
                  {
                    schoolOptions.find((school) => school.value === selectedSchool)
                      ?.label
                  }
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
        <Table
          bordered
          id="equity-benchmark-table-row"
          className="mb-0 custom-table-class"
        >
          <thead>
            <tr
              className="text-center sticky-tr"
              ref={(el) => {
                setHeaderHeight(el?.clientHeight ?? 0);
              }}
            >
              <th />
              {displayedEvidenceColumns.map((ec) => (
                <th key={ec.id}>
                  <OverlayTrigger
                    overlay={
                      <Tooltip id="fullName" className="customInfoTooltip">
                        {ec.measurement.display_name}
                      </Tooltip>
                    }
                  >
                    <span className="pdfFontFamily">{ec.measurement.column_name}</span>
                  </OverlayTrigger>
                </th>
              ))}
            </tr>
            <tr
              className="text-center sticky-tr"
              ref={(el) => {
                setAllStudentsHeight(el?.clientHeight ?? 0);
              }}
            >
              <td rowSpan={2} style={{ top: headerHeight }}>
                All Students
              </td>
              {displayedEvidenceColumns.map((ec) => {
                return (
                  <BellowCell
                    topOffset={headerHeight}
                    key={ec.id}
                    statsByEc={stats[ec.id]}
                    students={allStudents}
                  />
                );
              })}
            </tr>
            <tr className="text-center sticky-tr">
              {displayedEvidenceColumns.map((ec) => {
                return (
                  <AtOrAboveCell
                    topOffset={headerHeight + allStudentsHeight}
                    key={ec.id}
                    statsByEc={stats[ec.id]}
                    students={allStudents}
                  />
                );
              })}
            </tr>
          </thead>
          {Object.entries(raceDictionary).map(([race, students]) => {
            if (RaceDisplayedNames[+race as Race]) {
              return (
                <DemographicGroupRows
                  key={`race_${race}`}
                  groupKey={`race_${race}`}
                  label={
                    RaceDisplayedNames[+race as Race]
                      ? `${RaceDisplayedNames[+race as Race]} (Race)`
                      : "Unknown"
                  }
                  students={students}
                  evidenceColumns={displayedEvidenceColumns}
                  stats={stats}
                />
              );
            }
          })}
          {Object.entries(specialEducationDictionary).map(
            ([specialEducation, students]) => (
              <DemographicGroupRows
                key={`spacial_education_${specialEducation}`}
                groupKey={`spacial_education_${specialEducation}`}
                label="Special Education"
                students={students}
                evidenceColumns={displayedEvidenceColumns}
                stats={stats}
              />
            )
          )}
          {Object.entries(ELLDictionary).map(([ell, students]) => (
            <DemographicGroupRows
              key={`ell_${ell}`}
              groupKey={`ell_${ell}`}
              label="ELL (English Language Learner)"
              students={students}
              evidenceColumns={displayedEvidenceColumns}
              stats={stats}
            />
          ))}
          {Object.entries(SLDDictionary).map(([sld, students]) => (
            <DemographicGroupRows
              key={`sld_${sld}`}
              groupKey={`sld_${sld}`}
              label="SLD (Specific Learning Disability)"
              students={students}
              evidenceColumns={displayedEvidenceColumns}
              stats={stats}
            />
          ))}
          {Object.entries(genderDictionary).map(([gender, students]) => {
            if (GenderDisplayedNames[gender as Gender]) {
              return (
                <DemographicGroupRows
                  key={`gender_${gender}`}
                  groupKey={`gender_${gender}`}
                  label={`${
                    GenderDisplayedNames[gender as Gender]
                      ? GenderDisplayedNames[gender as Gender] + " (Gender)"
                      : "Unknown"
                  } `}
                  students={students}
                  evidenceColumns={displayedEvidenceColumns}
                  stats={stats}
                />
              );
            }
          })}
          {Object.entries(freeLunchDictionary).map(([freeLunch, students]) => (
            <DemographicGroupRows
              key={`freeLunch_${freeLunch}`}
              groupKey={`freeLunch_${freeLunch}`}
              label="Free or Reduced Lunch"
              students={students}
              evidenceColumns={displayedEvidenceColumns}
              stats={stats}
            />
          ))}
        </Table>
      </div>
    </>
  );
};

export default EquityBenchmarkTable;
